import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'signin' | 'signup';
}
export const AuthModal = ({
  isOpen,
  onClose,
  mode
}: AuthModalProps) => {
  const [currentMode, setCurrentMode] = useState(mode);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Focus trap implementation
  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current;
    if (!modal) return;
    // Focus the close button when modal opens
    closeButtonRef.current?.focus();
    const focusableElements = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);
  const handleSocialAuth = (provider: string) => {
    alert(`${provider} authentication requires backend OAuth configuration.\n\nTo implement:\n1. Set up OAuth app with ${provider}\n2. Configure backend OAuth endpoints\n3. Handle callback and token exchange\n\nFor demo purposes, this would redirect to your authentication server.`);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title" data-id="element-289">
      <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-slideUp" data-id="element-290">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-291">
          <h2 id="auth-modal-title" className="text-2xl font-bold text-gray-900" data-id="element-292">
            {currentMode === 'signin' ? 'Sign In' : 'Create Account'}
          </h2>
          <button ref={closeButtonRef} onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors duration-200" aria-label="Close authentication modal" data-id="element-293">
            <X size={24} data-id="element-294" />
          </button>
        </div>

        <div className="p-6" data-id="element-295">
          <p className="text-gray-600 mb-6" data-id="element-296">
            {currentMode === 'signin' ? 'Sign in to access your account and manage your loans' : 'Create an account to get started with Geldhaus'}
          </p>

          {/* Social Sign In Options */}
          <div className="space-y-3 mb-6" data-id="element-297">
            <button onClick={() => handleSocialAuth('Google')} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 group" aria-label="Sign in with Google" data-id="element-298">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" data-id="element-299">
                <path d="M19.8 10.2273C19.8 9.51819 19.7364 8.83637 19.6182 8.18182H10.2V12.05H15.6109C15.3727 13.3 14.6636 14.3591 13.6045 15.0682V17.5773H16.8273C18.7091 15.8364 19.8 13.2727 19.8 10.2273Z" fill="#4285F4" data-id="element-300" />
                <path d="M10.2 20C12.9 20 15.1727 19.1045 16.8273 17.5773L13.6045 15.0682C12.7091 15.6682 11.5636 16.0227 10.2 16.0227C7.59545 16.0227 5.38182 14.2636 4.58636 11.9H1.25455V14.4909C2.90909 17.7591 6.30909 20 10.2 20Z" fill="#34A853" data-id="element-301" />
                <path d="M4.58636 11.9C4.38636 11.3 4.27273 10.6591 4.27273 10C4.27273 9.34091 4.38636 8.7 4.58636 8.1V5.50909H1.25455C0.572727 6.85909 0.2 8.38636 0.2 10C0.2 11.6136 0.572727 13.1409 1.25455 14.4909L4.58636 11.9Z" fill="#FBBC05" data-id="element-302" />
                <path d="M10.2 3.97727C11.6864 3.97727 13.0182 4.48182 14.0682 5.47273L16.9227 2.61818C15.1682 0.981818 12.8955 0 10.2 0C6.30909 0 2.90909 2.24091 1.25455 5.50909L4.58636 8.1C5.38182 5.73636 7.59545 3.97727 10.2 3.97727Z" fill="#EA4335" data-id="element-303" />
              </svg>
              <span className="font-semibold text-gray-700 group-hover:text-primary-700" data-id="element-304">
                Continue with Google
              </span>
            </button>

            <button onClick={() => handleSocialAuth('Microsoft')} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 group" aria-label="Sign in with Microsoft" data-id="element-305">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" data-id="element-306">
                <path d="M0 0H9.5V9.5H0V0Z" fill="#F25022" data-id="element-307" />
                <path d="M10.5 0H20V9.5H10.5V0Z" fill="#7FBA00" data-id="element-308" />
                <path d="M0 10.5H9.5V20H0V10.5Z" fill="#00A4EF" data-id="element-309" />
                <path d="M10.5 10.5H20V20H10.5V10.5Z" fill="#FFB900" data-id="element-310" />
              </svg>
              <span className="font-semibold text-gray-700 group-hover:text-primary-700" data-id="element-311">
                Continue with Microsoft
              </span>
            </button>

            <button onClick={() => handleSocialAuth('Facebook')} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 group" aria-label="Sign in with Facebook" data-id="element-312">
              <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24" aria-hidden="true" data-id="element-313">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-314" />
              </svg>
              <span className="font-semibold text-gray-700 group-hover:text-primary-700" data-id="element-315">
                Continue with Facebook
              </span>
            </button>

            <button onClick={() => handleSocialAuth('TikTok')} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 group" aria-label="Sign in with TikTok" data-id="element-316">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-317">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-318" />
              </svg>
              <span className="font-semibold text-gray-700 group-hover:text-primary-700" data-id="element-319">
                Continue with TikTok
              </span>
            </button>
          </div>

          {currentMode === 'signup' && <>
              <div className="relative my-6" data-id="element-320">
                <div className="absolute inset-0 flex items-center" data-id="element-321">
                  <div className="w-full border-t border-gray-200" data-id="element-322"></div>
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-323">
                  <span className="px-4 bg-white text-gray-500" data-id="element-324">Optional</span>
                </div>
              </div>

              {/* Referral Code Input */}
              <div className="mb-6" data-id="element-325">
                <label htmlFor="referral-code" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-326">
                  Referral Code (Optional)
                </label>
                <input type="text" id="referral-code" name="referral-code" placeholder="Enter referral code" autoComplete="off" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-327" />
                <p className="text-xs text-gray-500 mt-2" data-id="element-328">
                  Have a referral code? Enter it to get special benefits!
                </p>
              </div>
            </>}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-329">
            <p className="text-sm text-blue-800" data-id="element-330">
              <strong data-id="element-331">Note:</strong> OAuth integration requires backend
              configuration. Contact your administrator to set up Google,
              Microsoft, Facebook, and TikTok authentication.
            </p>
          </div>

          <p className="text-xs text-gray-500 text-center" data-id="element-332">
            By continuing, you agree to Geldhaus's{' '}
            <a href="/legal/terms" className="text-primary-600 hover:underline" data-id="element-333">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/legal/privacy" className="text-primary-600 hover:underline" data-id="element-334">
              Privacy Policy
            </a>
          </p>

          {currentMode === 'signin' ? <p className="text-sm text-center mt-6 text-gray-600" data-id="element-335">
              Don't have an account?{' '}
              <button onClick={() => setCurrentMode('signup')} className="text-primary-600 font-semibold hover:underline" data-id="element-336">
                Sign up
              </button>
            </p> : <p className="text-sm text-center mt-6 text-gray-600" data-id="element-337">
              Already have an account?{' '}
              <button onClick={() => setCurrentMode('signin')} className="text-primary-600 font-semibold hover:underline" data-id="element-338">
                Sign in
              </button>
            </p>}
        </div>
      </div>
    </div>;
};